/**
 * 활동 화면의 목업 사례.
 * 고른 동안만 이 목록에 겹쳐 보이고, 플랜에는 저장하지 않는다.
 */
import { useSyncExternalStore } from "react";
import type { ActivityEvent, ActivityKind, ActivityTarget, ActivityValue } from "./activity-model";

export const ACTIVITY_MOCK_CASES = [
  { id: "many", label: "많이" },
  { id: "today", label: "오늘" },
  { id: "recent", label: "최근" },
  { id: "empty", label: "없음" },
] as const;

export type ActivityMockCase = "live" | (typeof ACTIVITY_MOCK_CASES)[number]["id"];

export interface ActivityMockPlan {
  id: string;
  name: string;
}

const LONG_PLAN = "2026년 4분기 그로스 캠페인과 파트너 공동 프로모션";
const LONG_SUBJECT = "인터뷰 녹취 파일 재무팀 분 누락과 재인터뷰 일정, 원본 보관 위치";

const MOCK_CASE_KEY = "activity-mock-case";

type Seed = {
  actor: string;
  kind: ActivityKind;
  subject: string;
  verb: string;
  delayed?: boolean;
  value?: ActivityValue;
  target: ActivityTarget;
};

const task = (id: string): ActivityTarget => ({ type: "task", taskId: id });
const comment = (id: string, commentId: string): ActivityTarget => ({ type: "task", taskId: id, commentId });
const record = (id: string): ActivityTarget => ({ type: "record", recordId: id });

/** 지연 말이 없는 줄. 오늘 사례는 이것만 써서 설명에서 지연을 뺀다. */
const NEUTRAL: Seed[] = [
  {
    actor: "최기획",
    kind: "task",
    subject: LONG_SUBJECT,
    verb: "에 댓글을 남겼습니다",
    target: comment("mock-task-interview", "mock-c1"),
  },
  {
    actor: "이회계",
    kind: "task",
    subject: LONG_SUBJECT,
    verb: "에 답글을 남겼습니다",
    target: comment("mock-task-interview", "mock-c2"),
  },
  {
    actor: "김PM",
    kind: "task",
    subject: "인터페이스 계정 매핑과 원가 코드 기준 확정",
    verb: " 상태를 진행 중으로 바꿨습니다",
    value: { type: "taskStatus", status: "inProgress" },
    target: task("mock-task-map"),
  },
  {
    actor: "박엔진",
    kind: "issue",
    subject: "재무 코드와 원가 코드가 서로 다른 계정",
    verb: " 상태를 대응 중으로 바꿨습니다",
    value: { type: "recordStatus", recordKind: "issue", status: "inProgress" },
    target: record("mock-issue-code"),
  },
  {
    actor: "정품질",
    kind: "issue",
    subject: "4월 마감 예외 기간",
    verb: " 심각도를 높음으로 바꿨습니다",
    value: { type: "severity", severity: "high" },
    target: record("mock-issue-close"),
  },
  {
    actor: "송디자인",
    kind: "task",
    subject: "현장 녹음과 회의 사진",
    verb: "에 사진을 올렸습니다 · 사진 3장",
    target: task("mock-task-photo"),
  },
  {
    actor: "한구매",
    kind: "document",
    subject: "매체 견적과 재인터뷰 비용 비교표",
    verb: "에 첨부를 추가했습니다",
    target: { type: "document", documentId: "mock-doc-quote" },
  },
  {
    actor: "오기획",
    kind: "document",
    subject: "2026년 4분기 인터뷰 녹취 정리와 재무팀 질문 목록 초안",
    verb: " 문서를 만들었습니다",
    target: { type: "document", documentId: "mock-doc-notes" },
  },
  {
    actor: "김PM",
    kind: "schedule",
    subject: "10월 4주차 주간 보고와 재인터뷰 일정 대조",
    verb: "를 만들었습니다",
    target: { type: "weekly", statusDate: "2026-10-31" },
  },
  {
    actor: "",
    kind: "baseline",
    subject: "2026년 10월 양산 기준선과 인터페이스 동결",
    verb: "을 저장했습니다",
    target: { type: "baseline", baselineId: "mock-baseline" },
  },
];

/** 지연이 설명에 남는 줄. 많이·최근만 넣는다. */
const DELAYED: Seed[] = [
  {
    actor: "강인프라",
    kind: "task",
    subject: "재인터뷰 녹음 장비",
    verb: " 상태를 막힘으로 바꿨습니다",
    delayed: true,
    value: { type: "taskStatus", status: "blocked" },
    target: task("mock-task-recorder"),
  },
  {
    actor: "김재무",
    kind: "issue",
    subject: "4월 마감",
    verb: "에 댓글을 남겼습니다",
    delayed: true,
    target: comment("mock-issue-slip", "mock-c3"),
  },
];

const FILLER: Seed[] = [
  { actor: "이디자인", kind: "task", subject: "키비주얼", verb: " 작업을 등록했습니다", target: task("mock-task-visual") },
  { actor: "박엔진", kind: "task", subject: "세그먼트", verb: " 작업을 등록했습니다", target: task("mock-task-segment") },
  { actor: "정프론트", kind: "document", subject: "브랜드 가이드", verb: " 공개로 바꿨습니다", target: { type: "document", documentId: "mock-doc-brand" } },
  { actor: "송DB", kind: "schedule", subject: "11월 1주차", verb: " 주간 보고를 마감했습니다", target: { type: "weekly", statusDate: "2026-11-07" } },
  {
    actor: "노현장",
    kind: "task",
    subject: "원본 반출",
    verb: " 상태를 막힘으로 바꿨습니다",
    delayed: true,
    value: { type: "taskStatus", status: "blocked" },
    target: task("mock-task-export"),
  },
  {
    actor: "최기획",
    kind: "task",
    subject: "질문 목록 마감",
    verb: "에 댓글을 남겼습니다",
    delayed: true,
    target: comment("mock-task-questions", "mock-c4"),
  },
];

function readStoredCase(): ActivityMockCase {
  try {
    const raw = sessionStorage.getItem(MOCK_CASE_KEY);
    if (raw === "many" || raw === "today" || raw === "recent" || raw === "empty" || raw === "live") return raw;
  } catch {
    // 이 환경에는 sessionStorage가 없다.
  }
  return "live";
}

let current: ActivityMockCase = readStoredCase();
const listeners = new Set<() => void>();

export function getActivityMockCase(): ActivityMockCase {
  return current;
}

export function setActivityMockCase(next: ActivityMockCase) {
  if (current === next) return;
  current = next;
  try {
    sessionStorage.setItem(MOCK_CASE_KEY, next);
  } catch {
    // 저장하지 못해도 이번 화면에는 적용한다.
  }
  for (const listener of listeners) listener();
}

export function useActivityMockCase(): ActivityMockCase {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getActivityMockCase,
    getActivityMockCase,
  );
}

export function activityMockEvents(
  scenario: Exclude<ActivityMockCase, "live">,
  options: { plans: ActivityMockPlan[]; focusPlanId?: string; now?: Date },
): ActivityEvent[] {
  if (scenario === "empty") return [];
  const now = options.now ?? new Date();
  const plans = resolvePlans(options.plans, options.focusPlanId);
  if (scenario === "today") return stamp(NEUTRAL, plans, now, 0);
  if (scenario === "recent") return stamp([...NEUTRAL, ...DELAYED], plans, now, -1);
  const today = stamp([...NEUTRAL, ...DELAYED, ...FILLER], plans, now, 0);
  const older = stamp(FILLER.slice(0, 2), plans, now, -10);
  return [...today, ...older].sort(byRecent);
}

function resolvePlans(plans: ActivityMockPlan[], focusPlanId?: string): ActivityMockPlan[] {
  if (focusPlanId) {
    const found = plans.find((plan) => plan.id === focusPlanId);
    return [{ id: focusPlanId, name: found?.name.trim() || "이 플랜" }];
  }
  const out: ActivityMockPlan[] = [];
  for (const plan of plans) {
    const name = plan.name.trim();
    if (!name || out.some((item) => item.id === plan.id)) continue;
    out.push({ id: plan.id, name });
  }
  if (out.length === 0) out.push({ id: "mock-plan-erp", name: "제조사 ERP 구축" });
  if (!out.some((plan) => plan.name === LONG_PLAN)) out.splice(Math.min(1, out.length), 0, { id: "mock-plan-long", name: LONG_PLAN });
  return out.slice(0, 4);
}

function stamp(seeds: Seed[], plans: ActivityMockPlan[], now: Date, dayOffset: number): ActivityEvent[] {
  return seeds.map((seed, index) => {
    const plan = plans[index % plans.length]!;
    const day = dayOffset === 0 ? 0 : dayOffset - Math.floor(index / 8);
    return {
      id: `mock:${dayOffset}:${index}:${seed.target.type}`,
      at: atLocal(now, day, 17 - Math.floor(index / 3), (40 - (index % 3) * 15 + 60) % 60),
      actor: seed.actor,
      planId: plan.id,
      planName: plan.name,
      kind: seed.kind,
      subject: seed.subject,
      verb: seed.verb,
      value: seed.value,
      delayed: seed.delayed ?? false,
      target: seed.target,
    };
  }).sort(byRecent);
}

function atLocal(now: Date, dayOffset: number, hour: number, minute: number): string {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayOffset, hour, minute, 0, 0).toISOString();
}

function byRecent(a: ActivityEvent, b: ActivityEvent): number {
  return a.at < b.at ? 1 : a.at > b.at ? -1 : b.id.localeCompare(a.id);
}
